import isEmpty from 'lodash/isEmpty';
import superagent from 'superagent';

const cachekey = '20170802';

/*
{
    onBuildRequest: null,
    onSendRequest: null,
    onHandleResponse: null,
}
*/
let globalRequestHooks = null;

type HandleResponseParams = {
    err?: any;
    reject: Function;
    resolve: Function;
    response: any;
    success404?: boolean;
    transform?: Function;
};

export const handleResponseWithNon200Errors = ({
    err,
    reject,
    resolve,
    response = {},
    success404,
    transform,
}: HandleResponseParams) => {
    /* eslint-disable-next-line */
    const rejectAndCaptureError = (errorToReturn, originalError = null) => {
        // We can capture the error here if we want to send the data to LogRocket
        // captureError(originalError || errorToReturn);
        return reject(errorToReturn);
    };

    const handleRequestError = () => {
        // If nothing could handle the response, and it was an error, reject the whole thing
        // Should we make this always be a string?
        if (typeof err === 'string') {
            return rejectAndCaptureError(err);
        } else {
            return rejectAndCaptureError('There was an error with the request', err);
        }
    };

    // If there is a server level error, reject the promise
    if (err) {
        if (success404 && err.status === 404) {
            return resolve(response.body || {});
        }
        // If there is not a  response, we can handle the error now. (CORS errors look like this)
        if (isEmpty(response)) {
            return handleRequestError();
        }
    }

    // This is a single weird request that we need to check for in the clerk console
    if (response && response.req && response.req.url && response.req.url.includes('bidmaster') && response.text) {
        try {
            return resolve(JSON.parse(response.text));
        } catch (parseError) {
            return resolve(false);
        }
    }

    let body = response.body || {};
    if (transform) {
        body = transform(body);
    }

    // Go ResponseEnvelope
    if ('error' in body && body.error) {
        // Special case with clerk commands where "currentState" is in meta on failure
        if ('meta' in body && body.meta && 'currentState' in body.meta) {
            return rejectAndCaptureError(body, body.payload);
        }
        return rejectAndCaptureError(body.payload);
    }

    // If the success and message properties are set in body (item-api response)
    // but success is falsy, reject the promise
    if ('success' in body && 'message' in body && !body.success) {
        return rejectAndCaptureError(body.message);
    }

    // If the success property is set in body but is falsy, reject the promise
    // Might be some sort of lambda (Flynn)
    if ('success' in body && !body.success) {
        if ('payload' in body) {
            if ('error' in body.payload) {
                // Check if the error is from the create account endpoint for the verify email screen
                if (body.payload.error === 'User Already Exists') {
                    return resolve(body);
                }
                return rejectAndCaptureError(body.payload.error);
            }
            return rejectAndCaptureError(body.payload);
        }
        return rejectAndCaptureError(body);
    }

    //if the response has an errorMessage it means it's broken
    if (Boolean(body.errorMessage)) {
        return rejectAndCaptureError(body.errorMessage);
    }

    if (err) {
        // If we have gotten this far and there was an err object, handle it
        return handleRequestError();
    }

    // else
    return resolve(body);
};

export const handleResponseOld = ({
    err,
    reject,
    resolve,
    response = {},
    success404,
    transform,
}: HandleResponseParams) => {
    // If there is a server level error, reject the promise
    if (err) {
        if (success404 && err.status === 404) {
            // dante gets to here and needs to be transformed.  look at how I do this in hardboiled - blake 2019-09-26
            return resolve(response.body || {});
        } else if (response?.body?.error && response?.body?.payload === 'User Already Exists') {
            // the server correctly responds with a 500 when the user exists
            return reject(response.body.payload);
        }
        return reject(err);
    }

    let body = response.body || {};
    if (transform) {
        body = transform(body);
    }

    // I saw a case where transform returned an undefined
    if (!body) {
        return reject('Unexpected error transforming response');
    }

    // If the success and message properties are set in body (item-api response)
    // but success is falsy, reject the promise
    if ('success' in body && 'message' in body && !body.success) {
        if (body?.error === 'Failed to authenticate a') {
            return reject(body.error);
        }
        return reject(body.message);
    }

    if ('success' in body && 'payload' in body && !body.success) {
        return reject(body.payload);
    }

    // Go ResponseEnvelope
    if ('error' in body && body.error) {
        if ('payload' in body) {
            // Proper go response is to have the payload be an error string when the error property is true
            if (typeof body.payload === 'string') {
                return reject(body.payload);
            }
            if (typeof body.payload === 'object') {
                // The createuser endpoint in flynn returns a non standard object when a bidder already exists
                if (body.payload.error === 'User Already Exists') {
                    return reject(body.payload.error);
                }
            }
        }
        // If the response is built correctly, the payload would have been a string and already rejected
        // but to keep the code backward compatible, we will reject with the payload anyway
        return reject(body.payload);
    }

    // If the success property is set in body but is falsy, reject the promise
    // Might be some sort of lambda (Flynn)
    if ('success' in body && !body.success) {
        if ('payload' in body) {
            if ('error' in body.payload) {
                return reject(body.payload.error);
            }
            return reject(body.payload);
        }
        return reject(body);
    }

    //if the response has an errorMessage it means it's broken
    if (Boolean(body.errorMessage)) {
        return reject(body.errorMessage);
    }
    // else
    return resolve(body);
};

export const handleResponseUseItemApiError = ({
    err,
    reject,
    resolve,
    response = {},
    success404,
    transform,
}: HandleResponseParams) => {
    // If there is a server level error, reject the promise
    if (err) {
        if (success404 && err.status === 404) {
            resolve(response.body || {});
        }
        return reject(err);
    }

    let body = response.body || {};
    if (transform) {
        body = transform(body);
    }

    // If the successa and message properties are set in body (item-api response)
    // but success is falsy, reject the promise
    if ('success' in body && 'error' in body && !body.success) {
        return reject(body.error);
    }

    // If the success property is set in body but is falsy, reject the promise
    // Might be some sort of lambda (Flynn)
    if ('success' in body && !body.success) {
        return reject(body);
    }

    // else
    return resolve(body);
};

const addCacheKey = (request) => {
    request.query({ c: cachekey });
    return request;
};

const hosts = [
    {
        dropDeploymentForProd: true,
        path: '<APPROVAL-API>',
        url: 'https://api-DEPLOYMENT.liveauctioneers.com/approval/APIPATH',
    },
    { path: '<ANNOUNCEMENTS-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/announcement-api/APIPATH' },
    { path: '<CATALOG-PREVIEWS-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/catalog/APIPATH' },
    { path: '<CATEGORY-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/category-api' },
    { path: '<COLLECTION-API>', url: 'https://api.liveauctioneers.com/collections/APIPATH/DEPLOYMENT' },
    { path: '<CURRENCY-API>', url: 'https://api.liveauctioneers.com/currency/DEPLOYMENT' },
    { path: '<HELP-SCOUT>', url: 'https://api.helpscout.net/v2/APIPATH' },
    { path: '<ITEM-API>', url: 'https://item-api-DEPLOYMENT.liveauctioneers.com/APIPATH' },
    { path: '<FP-API>', url: 'https://api.liveauctioneers.com/fp-DEPLOYMENT/APIPATH' },
    { path: '<MAINHOST-API>', url: 'https://mainhost-DEPLOYMENT.liveauctioneers.com/APIPATH' },
    { path: '<REDSTRIPE-API>', url: 'https://redstripe-DEPLOYMENT.liveauctioneers.com/payment-api/APIPATH' },
    {
        dropDeploymentForProd: true,
        path: '<MARKETPLACE-API>',
        url: 'https://api-DEPLOYMENT.liveauctioneers.com/marketplace/APIPATH',
    },
    { path: '<MESSAGING-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/messaging/APIPATH' },
    {
        path: '<MESSAGING-ATTACHMENTS-API>',
        url: 'https://messaging-api-DEPLOYMENT.liveauctioneers.com/messaging/APIPATH',
    },
    { path: '<NOTIFICATIONS-API>', url: 'https://telegram-DEPLOYMENT.liveauctioneers.com/' },
    {
        dropDeploymentForProd: true,
        path: '<PAYMENT-API>',
        url: 'https://api-DEPLOYMENT.liveauctioneers.com/payment/APIPATH',
    },
    {
        path: '<PERSONALIZATION-API>',
        url: 'https://api-DEPLOYMENT.liveauctioneers.com/personalization/APIPATH',
    },
    {
        path: '<PERSONALIZE-API>',
        url: 'https://api-DEPLOYMENT.liveauctioneers.com/personalize/recommend/APIPATH',
    },
    { path: '<PUBNUB-API>', url: 'https://ps.pndsn.com/APIPATH' },
    { path: '<ABLY-API>', url: 'https://pubnub.ably.io/APIPATH' },
    { path: '<PUSH-NOTIFICATION-API>', url: 'https://api.liveauctioneers.com/push/APIPATH/DEPLOYMENT' },
    {
        path: '<RECOMMENDATION-API>',
        url: 'https://api.liveauctioneers.com/recommendation/getrecommendations/DEPLOYMENT',
    },
    { path: '<REVIEW-API>', url: 'https://api.liveauctioneers.com/review/APIPATH/DEPLOYMENT' },
    { path: '<LEGACY-SAVED-SEARCH-API>', url: 'https://api.liveauctioneers.com/savedsearch/APIPATH/DEPLOYMENT' },
    { path: '<SAVED-SEARCH-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/savedsearch/APIPATH' },
    { path: '<SEARCH-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/search/APIPATH' },
    { path: '<AUCTION-ENGINE-API>', url: 'https://auction-service-DEPLOYMENT.liveauctioneers.com/APIPATH' },
    { path: '<SHIPPING-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/shipping/APIPATH' },
    { path: '<STATS-API>', url: 'https://crispy-pancake-DEPLOYMENT.liveauctioneers.com/APIPATH' },
    { path: '<STREAM-MANAGER>', url: 'https://api.liveauctioneers.com/stream/APIPATH/DEPLOYMENT' },
    { path: '<TAXES-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/taxes/APIPATH' },
    { path: '<ITEM-MODEL-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/content/APIPATH' },
    { path: '<SAVED-ITEMS-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/content/APIPATH' },
    { path: '<TITLE-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/title/APIPATH' },
    { path: '<USER-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/user/APIPATH' },
    { path: '<VARIANT-TEST-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/variant-test/APIPATH' },
    { path: '<AUCTION-CENTRAL-NEWS-API>', url: 'https://api-DEPLOYMENT.liveauctioneers.com/acn/APIPATH' },
];

export const buildUrl = (path, deployment, apiPath = '') => {
    let updatedPath = path;
    hosts.forEach((obj) => {
        if (path.includes(obj.path)) {
            let url;
            if (deployment === 'prod' && obj.dropDeploymentForProd) {
                if (obj.url.indexOf('-DEPLOYMENT') !== -1) {
                    url = obj.url.replace('-DEPLOYMENT', '');
                }
            } else {
                url = obj.url.replace('DEPLOYMENT', deployment);
            }
            updatedPath = path.replace(obj.path, url);
            updatedPath = updatedPath.replace('APIPATH', apiPath);
        }
    });
    return updatedPath;
};

const buildRequest = ({ apiPath, authToken, deployment, path, queryParams, requestFunc, useCacheKey = true }) => {
    const url = buildUrl(path, deployment, apiPath);
    const request = requestFunc(url);

    if (path && (path.includes('<ITEM-API>') || path.includes('s_msg') || path.includes('eoa_removal_response'))) {
        request.withCredentials();
    }

    // set our own useragent for the ssr
    if (typeof window === 'undefined') {
        request.set({ 'User-Agent': 'la-superagent' });
    }

    if (useCacheKey) {
        addCacheKey(request);
    }

    if (queryParams) {
        request.query(queryParams);
    }

    if (authToken) {
        request.set('Authorization', `Bearer ${authToken}`);
    }

    if (globalRequestHooks) {
        const { onBuildRequest, onHandleResponse, onSendRequest } = globalRequestHooks;
        onBuildRequest(request);

        const _end = request.end;
        request.end = (cb) => {
            onSendRequest(request, { url });
            return _end.bind(request)((error, response) => {
                cb(error, response);
                onHandleResponse(request, response, error);
            });
        };
    }

    return request;
};

type MakeParams = {
    apiPath?: string;
    authToken?: string;
    deployment?: string;
    path: string;
    queryParams?: any;
    useCacheKey?: boolean;
};

export const makeDelete = ({ apiPath, authToken, deployment, path, queryParams, useCacheKey = true }: MakeParams) =>
    buildRequest({ apiPath, authToken, deployment, path, queryParams, requestFunc: superagent.delete, useCacheKey });

export const makeGet = ({ apiPath, authToken, deployment, path, queryParams, useCacheKey = true }: MakeParams) =>
    buildRequest({ apiPath, authToken, deployment, path, queryParams, requestFunc: superagent.get, useCacheKey });

export const makePatch = ({ apiPath, authToken, deployment, path, queryParams, useCacheKey = true }: MakeParams) =>
    buildRequest({ apiPath, authToken, deployment, path, queryParams, requestFunc: superagent.patch, useCacheKey });

export const makePost = ({ apiPath, authToken, deployment, path, queryParams, useCacheKey = true }: MakeParams) =>
    buildRequest({ apiPath, authToken, deployment, path, queryParams, requestFunc: superagent.post, useCacheKey });

export const makePut = ({ apiPath, authToken, deployment, path, queryParams, useCacheKey = true }: MakeParams) =>
    buildRequest({ apiPath, authToken, deployment, path, queryParams, requestFunc: superagent.put, useCacheKey });

type SetGlobalRequestHooksParams = {
    onBuildRequest: Function;
    onHandleResponse: Function;
    onSendRequest: Function;
};

export const setGlobalRequestHooks = ({
    onBuildRequest,
    onHandleResponse,
    onSendRequest,
}: SetGlobalRequestHooksParams) => {
    globalRequestHooks = {
        onBuildRequest,
        onHandleResponse,
        onSendRequest,
    };
};
